import * as React from "react";
import { Button } from "../../button";
import { Input } from "../../input";
import { Switch } from "../../switch";
import { Icon } from "../../icon";
import { Plus } from "../../icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { apiCall } from "../../api/client";
import { ddl } from "../../api/contracts/database/ddl";
import type { RunResultDto } from "../../api/contracts/database/sql";

const SQLITE_TYPES = ["TEXT", "INTEGER", "REAL", "NUMERIC", "BLOB"];

/** Pull the first statement error out of a RunResult, if any. */
function firstError(res: RunResultDto): string | null {
  for (const s of res.statements) if (s.error) return s.error;
  return null;
}

interface FieldSpec {
  name: string;
  type: string;
  nullable: boolean;
  default: string;
}

function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return <p className="type-subtext text-destructive">{error}</p>;
}

/**
 * Structural editor for a table: add column, drop column, drop table — and
 * (table-agnostic) create table. Drops require typing the target name to
 * confirm, mirroring the Settings Danger-Zone pattern. Every change goes
 * through the gated DDL routes → runSql. See PRD §5.2, §9.2, §13.4.
 */
export function ColumnEditor({
  table,
  columns,
  onChanged,
}: {
  table: string;
  columns: string[];
  onChanged: () => void;
}) {
  const [open, setOpen] = React.useState<null | "add" | "drop-col" | "drop-table">(null);
  const close = () => setOpen(null);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon-tb"
          aria-label="Add column"
          title="Add column"
          onClick={() => setOpen("add")}
        >
          <Plus className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen("drop-col")}
          disabled={columns.length === 0}
        >
          <Icon name="minus" size="sm" /> Drop column
        </Button>
        <Button variant="outline" size="sm" onClick={() => setOpen("drop-table")}>
          <Icon name="trash-2" size="sm" /> Drop table
        </Button>
      </div>

      <AddColumnDialog
        table={table}
        open={open === "add"}
        onClose={close}
        onDone={onChanged}
      />
      <DropColumnDialog
        table={table}
        columns={columns}
        open={open === "drop-col"}
        onClose={close}
        onDone={onChanged}
      />
      <DropTableDialog
        table={table}
        open={open === "drop-table"}
        onClose={close}
        onDone={onChanged}
      />
    </>
  );
}

function AddColumnDialog({
  table,
  open,
  onClose,
  onDone,
}: {
  table: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [field, setField] = React.useState<FieldSpec>({
    name: "",
    type: "TEXT",
    nullable: true,
    default: "",
  });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setField({ name: "", type: "TEXT", nullable: true, default: "" });
      setError(null);
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall(ddl.addColumn, {
        body: {
          table,
          column: {
            name: field.name,
            type: field.type,
            notnull: !field.nullable,
            default: field.default || undefined,
          },
        },
      });
      const err = firstError(res);
      if (err) {
        setError(err);
        return;
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add column to {table}</DialogTitle>
          <DialogDescription>
            Live-only schema change — captured in the changelog, not the
            migration files.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="type-subtext text-muted-foreground">Name</span>
            <Input
              value={field.name}
              onChange={(e) => setField({ ...field, name: e.target.value })}
              placeholder="column_name"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="type-subtext text-muted-foreground">Type</span>
            <Select
              value={field.type}
              onValueChange={(v) => setField({ ...field, type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SQLITE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="add-col-nullable"
              checked={field.nullable}
              onCheckedChange={(c) => setField({ ...field, nullable: c })}
            />
            <label htmlFor="add-col-nullable" className="type-body text-muted-foreground">
              Nullable
            </label>
          </div>
          <div className="flex flex-col gap-1">
            <span className="type-subtext text-muted-foreground">
              Default (optional — a number, CURRENT_TIMESTAMP, or a value)
            </span>
            <Input
              value={field.default}
              onChange={(e) => setField({ ...field, default: e.target.value })}
              placeholder="e.g. 0 or 'pending'"
            />
          </div>
          <ErrorNote error={error} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !field.name.trim()}>
            {busy ? "Adding…" : "Add column"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropColumnDialog({
  table,
  columns,
  open,
  onClose,
  onDone,
}: {
  table: string;
  columns: string[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [col, setCol] = React.useState<string>("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setCol("");
      setConfirm("");
      setError(null);
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall(ddl.dropColumn, {
        body: { table, column: col, confirm: true },
      });
      const err = firstError(res);
      if (err) {
        setError(err);
        return;
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Drop a column from {table}</DialogTitle>
          <DialogDescription>
            This permanently removes the column and its data. Type the column
            name to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Select value={col} onValueChange={setCol}>
            <SelectTrigger>
              <SelectValue placeholder="Select a column" />
            </SelectTrigger>
            <SelectContent>
              {columns.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={col ? `Type "${col}" to confirm` : "Select a column first"}
            disabled={!col}
          />
          <ErrorNote error={error} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={busy || !col || confirm !== col}
          >
            {busy ? "Dropping…" : "Drop column"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropTableDialog({
  table,
  open,
  onClose,
  onDone,
}: {
  table: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setConfirm("");
      setError(null);
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall(ddl.dropTable, {
        body: { table, confirm: true },
      });
      const err = firstError(res);
      if (err) {
        setError(err);
        return;
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Drop table {table}</DialogTitle>
          <DialogDescription>
            This permanently deletes the table and all of its rows. Type the
            table name to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={`Type "${table}" to confirm`}
            autoFocus
          />
          <ErrorNote error={error} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={busy || confirm !== table}
          >
            {busy ? "Dropping…" : "Drop table"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Table-agnostic create-table dialog. Used by the Table Editor's empty state /
 * "New table" action.
 */
export function CreateTableDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (table: string) => void;
}) {
  const [name, setName] = React.useState("");
  const [fields, setFields] = React.useState<FieldSpec[]>([
    { name: "id", type: "INTEGER", nullable: false, default: "" },
  ]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName("");
      setFields([{ name: "id", type: "INTEGER", nullable: false, default: "" }]);
      setError(null);
    }
  }, [open]);

  function updateField(i: number, patch: Partial<FieldSpec>) {
    setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall(ddl.createTable, {
        body: {
          table: name,
          columns: fields
            .filter((f) => f.name.trim())
            .map((f) => ({
              name: f.name,
              type: f.type,
              notnull: !f.nullable,
              default: f.default || undefined,
            })),
        },
      });
      const err = firstError(res);
      if (err) {
        setError(err);
        return;
      }
      onDone(name);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create table</DialogTitle>
          <DialogDescription>
            Live-only — captured in the changelog, not the migration files.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="table_name"
            autoFocus
          />
          <div className="flex flex-col gap-2">
            {fields.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={f.name}
                  onChange={(e) => updateField(i, { name: e.target.value })}
                  placeholder="column"
                  className="flex-1"
                />
                <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SQLITE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFields((fs) => fs.filter((_, j) => j !== i))}
                  disabled={fields.length === 1}
                  aria-label="Remove column"
                >
                  <Icon name="x" size="sm" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setFields((fs) => [
                  ...fs,
                  { name: "", type: "TEXT", nullable: true, default: "" },
                ])
              }
            >
              <Icon name="plus" size="sm" /> Add column
            </Button>
          </div>
          <ErrorNote error={error} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create table"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
